import React, { useMemo, useState } from 'react';

import type { UUID } from '@aline/common';
import { Skeleton } from '@mui/material';
import { AlignLeft, FileText, ListChecks, MessageSquare, Users } from 'lucide-react';
import { ActivityFeed, ActivitySubscribers } from 'src/components';
import { isBlankText } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/utils/empty-paragraph';
import { ActivityLaneSwitch } from 'src/components/workflow/workflow-detail/side-panel/task-review/ActivityLaneSwitch';
import { TaskComposerDraftContext } from 'src/components/workflow/workflow-detail/side-panel/task-review/task-composer-draft';
import {
  getRequesterAssignment,
  getTaskDocuments,
  getTaskIsOpen,
} from 'src/components/workflow/workflow-detail/side-panel/task-review/task-review-helpers';
import { TaskActivityComposer } from 'src/components/workflow/workflow-detail/side-panel/task-review/TaskActivityComposer';
import { TaskAiAssigneeRow } from 'src/components/workflow/workflow-detail/side-panel/task-review/TaskAiAssigneeRow';
import { TaskBreadcrumbs } from 'src/components/workflow/workflow-detail/side-panel/task-review/TaskBreadcrumbs';
import type { TaskDocument } from 'src/components/workflow/workflow-detail/side-panel/task-review/TaskDocuments';
import { TaskDocuments } from 'src/components/workflow/workflow-detail/side-panel/task-review/TaskDocuments';
import { useTaskNextStep } from 'src/components/workflow/workflow-detail/side-panel/task-review/TaskNextStep';
import { TaskReviewAssignments } from 'src/components/workflow/workflow-detail/side-panel/task-review/TaskReviewAssignments';
import { TaskReviewFooter } from 'src/components/workflow/workflow-detail/side-panel/task-review/TaskReviewFooter';
import { TaskReviewStatusAlert } from 'src/components/workflow/workflow-detail/side-panel/task-review/TaskReviewStatusAlert';
import { TaskSubtaskList } from 'src/components/workflow/workflow-detail/side-panel/task-review/TaskSubtaskList';
import type { ActivityLane } from 'src/components/workflow/workflow-detail/side-panel/task-review/use-activity-lane';
import { ACTIVITY_LANE } from 'src/components/workflow/workflow-detail/side-panel/task-review/use-activity-lane';
import type {
  TaskPanelState,
  TaskWithComments,
} from 'src/components/workflow/workflow-detail/side-panel/task-review/use-task-panel-state';
import { useTaskPanelState } from 'src/components/workflow/workflow-detail/side-panel/task-review/use-task-panel-state';
import { TaskAssigneePicker } from 'src/components/workflow/workflow-detail/side-panel/TaskAssigneePicker';
import { TaskDescriptionField } from 'src/components/workflow/workflow-detail/side-panel/TaskDescriptionField';
import { TaskInstructions } from 'src/components/workflow/workflow-detail/side-panel/TaskInstructions';
import { useCurrentUserId } from 'src/gql';
import { makeStyles } from 'tss-react/mui';

import { getExternalTaskSubtitle } from './task-page-helpers';
import { TaskPageCard, TaskPageCardTitle } from './TaskPageCard';
import { TaskPageHeader } from './TaskPageHeader';
import { TaskReviewDetails } from './TaskReviewDetails';
const TASK_PAGE_MAX_WIDTH = 1120;

const SIDEBAR_WIDTH = 300;
// Below this the sidebar stacks under the cards.
const STACK_BREAKPOINT = 960;
const WORD_TIP = 'Prefer Word? Download a document, redline it and upload it back from its card.';
const LANE_CAPTION = {
  [ACTIVITY_LANE.External]: 'External reviewers can see everything in this lane.',
  [ACTIVITY_LANE.Team]: 'Only your organization sees this lane.',
} satisfies Record<ActivityLane, string>;
const DESCRIPTION_HEADING = <TaskPageCardTitle icon={AlignLeft}>Description</TaskPageCardTitle>;
const ASSIGNEES_HEADING = <TaskPageCardTitle icon={Users}>Assignees</TaskPageCardTitle>;
const SUBTASKS_HEADING = <TaskPageCardTitle icon={ListChecks}>Sub-tasks</TaskPageCardTitle>;

const useStyles = makeStyles()(theme => ({
  grid: {
    alignItems: 'start',
    display: 'grid',
    gap: theme.spacing(1.5),
    gridTemplateColumns: `minmax(0, 1fr) ${SIDEBAR_WIDTH}px`,
    [`@media (max-width: ${STACK_BREAKPOINT}px)`]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  inner: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    margin: '0 auto',
    maxWidth: TASK_PAGE_MAX_WIDTH,
    padding: theme.spacing(3, 3, 4),
    width: '100%',
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  root: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  // The activity feed scrolls with the page and keeps its own place as rows load above.
  scroller: {
    backgroundColor: theme.appColors.darkBackground,
    flex: 1,
    minHeight: 0,
    overflowAnchor: 'none',
    overflowY: 'auto',
  },
  sidebar: {
    position: 'sticky',
    top: theme.spacing(1),
    [`@media (max-width: ${STACK_BREAKPOINT}px)`]: {
      position: 'static',
    },
  },
  // Mirrors the bottom action bar of TaskReviewFooter.
  skeletonBar: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    borderTop: `1px solid ${theme.appColors.ruleSubtle}`,
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    minHeight: 64,
    padding: theme.spacing(1.5, 3),
  },
  skeletonColumn: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  skeletonDetailRow: {
    '&:last-child': {
      borderBottom: 'none',
    },
    alignItems: 'center',
    borderBottom: `1px solid ${theme.appColors.ruleSubtle}`,
    display: 'flex',
    gap: theme.spacing(1),
    minHeight: 44,
  },
  skeletonDocuments: {
    display: 'grid',
    gap: theme.spacing(1.5),
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  },
  skeletonRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1.5),
  },
}));

const SKELETON_ASSIGNEES = 2;
const SKELETON_DETAIL_ROWS = 5;
const SKELETON_DOCUMENTS = 2;

interface TaskPageLayoutProps {
  onOpenTask: (taskId: UUID) => void;
  task: TaskWithComments;
  workflowId: UUID;
  isPreview?: boolean;
}

/** The task's own page: a header card, the task as cards, a Review details sidebar, and actions. */
export function TaskPageLayout({
  isPreview = false,
  onOpenTask,
  task,
  workflowId,
}: TaskPageLayoutProps): JSX.Element {
  const { classes } = useStyles();
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  const state = useTaskPanelState({
    isAiBuilding: false,
    isPreview,
    task,
    workflowId,
  });
  const { isExternalView, isReadOnly } = state;
  const documents = useMemo(() => getTaskDocuments(task), [task]);
  const nextStep = useTaskNextStep({
    documentCount: documents.length,
    isExternalView,
    isPreview,
    task,
  });
  const editDisabledReason = isPreview ? 'Preview only' : task.requesterDisallowedReason.edit;
  const subtitle = isExternalView
    ? getExternalTaskSubtitle({ documentCount: documents.length, ownerName: task.ownerName })
    : [task.workflow.name, task.phase.name].filter(Boolean).join(' · ');
  const cardsProps = {
    documents,
    editDisabledReason,
    isPreview,
    onOpenTask,
    scrollElement,
    state,
    task,
    workflowId,
  };

  return (
    <TaskComposerDraftContext value={state.composerDraft}>
      <div className={classes.root}>
        <div ref={setScrollElement} className={classes.scroller}>
          <div key={task.id} className={classes.inner}>
            {task.parentTask && (
              <TaskBreadcrumbs
                parentTask={task.parentTask}
                title={task.title}
                onOpenTask={onOpenTask}
              />
            )}
            <TaskPageHeader
              editDisabledReason={editDisabledReason}
              isReadOnly={isReadOnly || isExternalView}
              subtitle={subtitle}
              taskId={task.id}
              title={task.title}
            />
            {!isExternalView && (
              <TaskReviewStatusAlert
                task={task}
                onJumpToReviewers={
                  task.assignments.length ? () => state.reviewersRef.current?.reveal() : null
                }
              />
            )}
            <div className={classes.grid}>
              <div className={classes.main}>
                <TaskPageCards {...cardsProps} />
              </div>
              <aside className={classes.sidebar}>
                <TaskReviewDetails documentCount={documents.length} task={task} />
              </aside>
            </div>
          </div>
        </div>
        <TaskReviewFooter
          isPreview={isPreview}
          lead={nextStep}
          readOnly={false}
          task={task}
          variant="bar"
          workflow={task.workflow}
        />
      </div>
    </TaskComposerDraftContext>
  );
}

/** The task page's shape while the task loads: header, cards, Review details and the action bar. */
export function TaskPageSkeleton(): JSX.Element {
  const { classes } = useStyles();

  return (
    <div aria-busy="true" className={classes.root}>
      <div className={classes.scroller}>
        <div className={classes.inner}>
          <TaskPageCard>
            <div className={classes.skeletonRow}>
              <Skeleton height={56} sx={{ borderRadius: '12px' }} variant="rounded" width={56} />
              <div className={classes.skeletonColumn}>
                <Skeleton height={32} variant="text" width="45%" />
                <Skeleton height={20} variant="text" width="30%" />
              </div>
            </div>
          </TaskPageCard>
          <div className={classes.grid}>
            <div className={classes.main}>
              <SkeletonCard titleWidth={110}>
                <div className={classes.skeletonColumn}>
                  <Skeleton height={20} variant="text" width="92%" />
                  <Skeleton height={20} variant="text" width="80%" />
                  <Skeleton height={20} variant="text" width="55%" />
                </div>
              </SkeletonCard>
              <SkeletonCard titleWidth={180}>
                <div className={classes.skeletonDocuments}>
                  {Array.from({ length: SKELETON_DOCUMENTS }, (_, index) => (
                    <Skeleton key={index} height={72} variant="rounded" />
                  ))}
                </div>
              </SkeletonCard>
              <SkeletonCard titleWidth={100}>
                {Array.from({ length: SKELETON_ASSIGNEES }, (_, index) => (
                  <div key={index} className={classes.skeletonRow}>
                    <Skeleton height={32} variant="circular" width={32} />
                    <div className={classes.skeletonColumn}>
                      <Skeleton height={20} variant="text" width="35%" />
                      <Skeleton height={16} variant="text" width="20%" />
                    </div>
                    <Skeleton
                      height={24}
                      sx={{ borderRadius: '12px' }}
                      variant="rounded"
                      width={72}
                    />
                  </div>
                ))}
              </SkeletonCard>
              <SkeletonCard titleWidth={80}>
                <Skeleton height={160} variant="rounded" />
                <Skeleton height={44} variant="rounded" />
              </SkeletonCard>
            </div>
            <aside className={classes.sidebar}>
              <SkeletonCard titleWidth={130}>
                <div>
                  {Array.from({ length: SKELETON_DETAIL_ROWS }, (_, index) => (
                    <div key={index} className={classes.skeletonDetailRow}>
                      <Skeleton height={16} variant="circular" width={16} />
                      <Skeleton height={20} sx={{ flex: 1 }} variant="text" />
                      <Skeleton height={20} variant="text" width={48} />
                    </div>
                  ))}
                </div>
              </SkeletonCard>
            </aside>
          </div>
        </div>
      </div>
      <div className={classes.skeletonBar}>
        <div className={classes.skeletonRow}>
          <Skeleton height={36} variant="rounded" width={36} />
          <div className={classes.skeletonColumn}>
            <Skeleton height={20} variant="text" width={160} />
            <Skeleton height={16} variant="text" width={220} />
          </div>
        </div>
        <div className={classes.skeletonRow}>
          <Skeleton height={36} variant="rounded" width={120} />
          <Skeleton height={36} variant="rounded" width={120} />
        </div>
      </div>
    </div>
  );
}

interface TaskPageCardsProps {
  documents: TaskDocument[];
  editDisabledReason: string | null | undefined;
  isPreview: boolean;
  onOpenTask: (taskId: UUID) => void;
  scrollElement: HTMLElement | null;
  state: TaskPanelState;
  task: TaskWithComments;
  workflowId: UUID;
}

function TaskPageCards(props: TaskPageCardsProps): JSX.Element {
  if (props.state.isExternalView) return <ExternalTaskCards {...props} />;
  return <InternalTaskCards {...props} />;
}

/** What external reviewers, and the preview, see: their instructions, the documents, activity. */
function ExternalTaskCards({
  documents,
  isPreview,
  scrollElement,
  state,
  task,
}: TaskPageCardsProps): JSX.Element {
  return (
    <>
      {!isBlankText(task.externalDescription) && (
        <TaskPageCard icon={AlignLeft} title="Instructions">
          <TaskInstructions hideLabel externalDescription={task.externalDescription} />
        </TaskPageCard>
      )}
      <TaskDocumentsCard documents={documents} isPreview={isPreview} state={state} task={task} />
      {!state.isTemplateTask && (
        <TaskActivityCard
          isPreview={isPreview}
          scrollElement={scrollElement}
          state={state}
          task={task}
        />
      )}
    </>
  );
}

function InternalTaskCards({
  documents,
  editDisabledReason,
  isPreview,
  onOpenTask,
  scrollElement,
  state,
  task,
  workflowId,
}: TaskPageCardsProps): JSX.Element {
  const { isReadOnly } = state;
  const canAddSubtask = !isReadOnly && !editDisabledReason;
  const showsSubtasks = !task.parentTask && (task.subtasks.length > 0 || canAddSubtask);

  return (
    <>
      <TaskPageCard>
        <TaskDescriptionField
          description={task.description}
          editDisabledReason={editDisabledReason}
          externalDescription={task.externalDescription}
          heading={DESCRIPTION_HEADING}
          isReadOnly={isReadOnly}
          taskId={task.id}
          workflowId={workflowId}
        />
      </TaskPageCard>
      <TaskDocumentsCard documents={documents} isPreview={isPreview} state={state} task={task} />
      <TaskPageCard>
        <TaskReviewAssignments
          ref={state.reviewersRef}
          aiRow={
            task.isDelegatedToAi && (
              <TaskAiAssigneeRow
                editDisabledReason={editDisabledReason}
                isReadOnly={isReadOnly}
                taskId={task.id}
              />
            )
          }
          assigneePicker={
            <TaskAssigneePicker
              editDisabledReason={editDisabledReason}
              isReadOnly={isReadOnly}
              task={task}
              workflowId={workflowId}
              onAlineAiActivityStarted={state.handleAlineAiActivityStarted}
            />
          }
          changeStatusDisabledReason={
            isPreview ? 'Preview only' : (task.requesterDisallowedReason.changeStatus ?? null)
          }
          editDisabledReason={editDisabledReason ?? null}
          heading={ASSIGNEES_HEADING}
          isReadOnly={isReadOnly}
          task={task}
          workflowId={workflowId}
        />
      </TaskPageCard>
      {showsSubtasks && (
        <TaskPageCard>
          <TaskSubtaskList
            canEdit={canAddSubtask}
            heading={SUBTASKS_HEADING}
            parentTaskId={task.id}
            phaseId={task.phase.id}
            subtasks={task.subtasks}
            workflowId={workflowId}
            onOpenTask={onOpenTask}
          />
        </TaskPageCard>
      )}
      {!state.isTemplateTask && (
        <TaskActivityCard
          isPreview={isPreview}
          scrollElement={scrollElement}
          state={state}
          task={task}
        />
      )}
    </>
  );
}

function TaskDocumentsCard({
  documents,
  isPreview,
  state,
  task,
}: Pick<TaskPageCardsProps, 'documents' | 'isPreview' | 'state' | 'task'>): JSX.Element | null {
  const currentUserId = useCurrentUserId();
  if (!documents.length) return null;
  const assignment = isPreview
    ? (task.assignments.find(item => item.isExternal) ?? null)
    : getRequesterAssignment(task.assignments, currentUserId);
  const isReviewing = Boolean(assignment) && !assignment?.decision && getTaskIsOpen(task);

  return (
    <TaskPageCard
      caption={isReviewing && WORD_TIP}
      count={documents.length}
      icon={FileText}
      title="Documents to review"
    >
      <TaskDocuments
        hideHeader
        currentDocumentId={null}
        documents={documents}
        downloadCurrentDocument={undefined}
        isPreview={isPreview}
        isReadOnly={state.isReadOnly}
        task={task}
      />
    </TaskPageCard>
  );
}

function TaskActivityCard({
  isPreview,
  scrollElement,
  state,
  task,
}: Pick<TaskPageCardsProps, 'isPreview' | 'scrollElement' | 'state' | 'task'>): JSX.Element {
  const { activityController, activityLane, isExternalView } = state;

  return (
    <TaskPageCard
      caption={activityLane.showSwitch && LANE_CAPTION[activityLane.lane]}
      headerAction={!isExternalView && <ActivitySubscribers taskId={task.id} />}
      headerControl={
        activityLane.showSwitch && (
          <ActivityLaneSwitch lane={activityLane.lane} onChange={activityLane.setLane} />
        )
      }
      icon={MessageSquare}
      title="Activity"
    >
      {/* Each lane is its own timeline, so switching starts its feed over. */}
      {scrollElement instanceof HTMLElement && (
        <ActivityFeed
          key={activityLane.lane}
          ref={state.feedRef}
          fetchMoreActivity={activityController.fetchMoreActivity}
          fetchPreviousActivity={activityController.fetchPreviousActivity}
          hasMore={activityController.hasMore}
          hasPrevious={activityController.hasPrevious}
          items={activityController.activity}
          loadedAnchor={activityController.loadedAnchor}
          loading={activityController.loading}
          loadingMore={activityController.loadingMore}
          loadingPrevious={activityController.loadingPrevious}
          navigation={activityController.navigation}
          // Read from the top like the rest of the page: oldest first, newer rows load below.
          opensAtNewest={false}
          scrollElement={scrollElement}
          showHeader={false}
          workflowId={activityController.workflowId}
          onSubmitReply={activityController.onSubmitReply}
          onViewThread={state.onViewThread}
        />
      )}
      <TaskActivityComposer isPreview={isPreview} state={state} task={task} />
    </TaskPageCard>
  );
}

/** A card with a placeholder icon and title above its content. */
function SkeletonCard({
  children,
  titleWidth,
}: {
  children: React.ReactNode;
  titleWidth: number;
}): JSX.Element {
  const { classes } = useStyles();

  return (
    <TaskPageCard>
      <div className={classes.skeletonRow}>
        <Skeleton height={18} variant="rounded" width={18} />
        <Skeleton height={24} variant="text" width={titleWidth} />
      </div>
      {children}
    </TaskPageCard>
  );
}
